import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField } from '@material-ui/core'
import React, { useEffect, useState } from 'react'
import { Product_product_listProductApplications_productApplications } from '../hooks/__generated__/Product'
import { TruncatedId } from '../../truncated-id'
import { FormattedDateTime } from '../../formatted-date-time'
import { ApolloConsumer } from '@apollo/react-common'
import { Check, GetApp } from '@material-ui/icons'
import { LoadMask, useToasts } from '@vestaboard/installables'
import { ProductApplicationSortBy, SortOrder } from '../types'
import { SuperGrid } from '../../../ui/SuperGrid'
import { csvDownload, csvFormat } from '../../../utils'
import { useListProductApplications, QUERY as PLUS_PRODUCT_QUERY } from '../hooks/useListProductApplications'
import moment from 'moment'

const PRODUCT_ID = 'ba25bb32-7a28-4a69-a34b-87a3fdeaadd6' // The Vestaboard Plus Product ID

interface FlatPlusApplication {
  subscriptionId: string
  boardId?: string
  personId?: string
  email?: string
  purchaseDate: number
  activationDate: number
  nextBillingDate: number
  pricingType: string
  activationCode: string
  optOutOfAutoRenewal: boolean
  optOutReason?: string
  optOutDate?: number
  shopifyOrderId?: string
  shopifyOrderName?: string
}

interface FlatPlusApplicationCsv {
  subscriptionId: string
  boardId?: string
  personId?: string
  email?: string
  purchaseDate: string
  activationDate: string
  nextBillingDate: string
  pricingType: string
  activationCode: string
  optOutOfAutoRenewal: boolean
  optOutReason?: string
  optOutDate?: number
  shopifyOrderId?: string
  shopifyOrderName?: string
}

const columns = [
  {
    title: 'Subscription ID',
    key: ProductApplicationSortBy.id,
    render: (plusApplication: FlatPlusApplication) => (
      <TruncatedId value={plusApplication.subscriptionId} path={`/plus/${plusApplication.subscriptionId}`} />
    ),
    width: 150
  },
  {
    title: 'Board ID',
    key: ProductApplicationSortBy.boardId,
    render: (plusApplication: FlatPlusApplication) => (
      <TruncatedId value={plusApplication?.boardId || ''} path={`/boards/${plusApplication?.boardId}`} />
    ),
    width: 150
  },
  {
    title: 'Purchase Date',
    key: ProductApplicationSortBy.created,
    render: (plusApplication: FlatPlusApplication) => (
      <FormattedDateTime format='MM/DD/YYYY' value={plusApplication.purchaseDate} />
    ),
    width: 100
  },
  {
    title: 'Activation Date',
    key: ProductApplicationSortBy.effectiveOnOrAfter,
    render: (plusApplication: FlatPlusApplication) => (
      <FormattedDateTime format='MM/DD/YYYY' value={plusApplication.activationDate} />
    ),
    width: 140
  },
  {
    title: 'Next Billing Date',
    key: ProductApplicationSortBy.expiresOnOrAfter,
    render: (plusApplication: FlatPlusApplication) => (
      <FormattedDateTime format='MM/DD/YYYY' value={plusApplication.nextBillingDate} />
    ),
    width: 180
  },
  {
    title: 'Pricing Type',
    key: ProductApplicationSortBy.pricingType,
    render: (plusApplication: FlatPlusApplication) => plusApplication.pricingType,
    width: 120
  },
  {
    title: 'Person ID',
    key: ProductApplicationSortBy.personId,
    render: (plusApplication: FlatPlusApplication) =>
      plusApplication?.personId ? (
        <TruncatedId value={plusApplication?.personId || ''} path={`/person/${plusApplication?.personId}`} />
      ) : null,
    width: 140
  },
  {
    title: 'Person Email',
    key: ProductApplicationSortBy.email,
    render: (plusApplication: FlatPlusApplication) => plusApplication.email || '',
    width: 200
  },
  {
    title: 'Activation Code',
    key: ProductApplicationSortBy.activationCode,
    render: (plusApplication: FlatPlusApplication) => plusApplication?.activationCode,
    width: 140
  },
  {
    title: 'Opted Out of Renewal',
    key: ProductApplicationSortBy.optOutOfRenewal,
    render: (plusApplication: FlatPlusApplication) => (plusApplication?.optOutOfAutoRenewal ? <Check /> : null),
    width: 170
  },
  {
    title: 'Opted Out Date',
    key: ProductApplicationSortBy.optOutDate,
    render: (plusApplication: FlatPlusApplication) => (
      <FormattedDateTime format='MM/DD/YYYY' value={plusApplication.optOutDate} />
    ),
    width: 120
  },
  {
    title: 'Opted Reason',
    key: ProductApplicationSortBy.optOutReason,
    render: (plusApplication: FlatPlusApplication) => plusApplication.optOutReason,
    width: 200
  },
  {
    title: 'Shopify Order ID',
    key: ProductApplicationSortBy.shopifyOrderId,
    render: (plusApplication: FlatPlusApplication) => (
      <a
        style={{
          color: '#3f51b5',
          fontSize: '1rem',
          textDecoration: 'none'
        }}
        target='_blank'
        rel='noopener noreferrer'
        href={`https://vestaboard-plus.myshopify.com/admin/orders/${plusApplication?.shopifyOrderId}`}>
        {plusApplication?.shopifyOrderId || ''}
      </a>
    ),
    width: 120
  },
  {
    title: 'Shopify Order Name',
    key: ProductApplicationSortBy.shopifyOrderName,
    render: (plusApplication: FlatPlusApplication) => plusApplication?.shopifyOrderName,
    width: 170
  }
]

export const PlusList = () => {
  const perPage = 50
  const [sort, setSort] = useState<{
    by: ProductApplicationSortBy
    order: SortOrder
  }>({
    by: ProductApplicationSortBy.effectiveOnOrAfter,
    order: SortOrder.desc
  })
  const [searchValue, setSearchValue] = useState<string | null>(null)
  const [page, setPage] = useState(0)
  const [exportDialogOpen, setExportDialogOpen] = useState(false)
  const [exportDateMin, setExportDateMin] = useState('')
  const [exportDateMax, setExportDateMax] = useState('')
  const [exportDateField, setExportDateField] = useState<'created' | 'started' | 'ends'>('created')
  const [exporting, setExporting] = useState(false)
  const { addToast } = useToasts()
  const variables = {
    id: PRODUCT_ID,
    page,
    perPage,
    terms: (searchValue || '').trim(),
    sortBy: sort.by,
    sortOrder: sort.order
  }
  const { data, loading, error } = useListProductApplications(variables)

  useEffect(() => {}, [page, perPage, searchValue, sort])

  const plusApplications = data?.product.listProductApplications.productApplications || []
  const pagination = data?.product.listProductApplications?.pagination

  const createFlatPurchase = (app: Product_product_listProductApplications_productApplications) =>
    ({
      subscriptionId: app.id,
      boardId: app.board?.id,
      personId: app.purchaseLineItem?.purchase?.account?.person?.id,
      email: app.purchaseLineItem?.purchase?.emailAddress,
      purchaseDate: moment(app.created).format('YYYY-MM-DD hh:mm a'),
      activationDate: moment(app.started).format('YYYY-MM-DD hh:mm a'),
      nextBillingDate: moment(app.ends).format('YYYY-MM-DD hh:mm a'),
      pricingType: app.pricing?.title || '',
      activationCode: app.activationCode?.code || '',
      optOutOfAutoRenewal: app.optOutOfAutoRenewal,
      optOutReason: app.optOutReason,
      optOutDate: app.optOutDate,
      shopifyOrderId: app.purchaseLineItem?.purchase?.purchaseSession?.shopifyOrderId,
      shopifyOrderName: app.purchaseLineItem?.purchase?.purchaseSession?.shopifyOrderName
    } as FlatPlusApplicationCsv)

  const visiblePlusApplications = (plusApplications || []).map(createFlatPurchase)

  return (
    <>
      <SuperGrid
        title='Activated Subscriptions'
        setSort={setSort}
        columns={columns}
        rows={visiblePlusApplications}
        sortBy={sort.by}
        sortOrder={sort.order}
        pagination={pagination}
        setPage={setPage}
        error={!!error}
        loading={loading}
        searchValue={searchValue}
        setSearchValue={setSearchValue}
        centerButtons={
          <Box display='flex'>
            <Button variant='outlined' onClick={() => setExportDialogOpen(true)}>
              Export CSV
            </Button>
            <Dialog open={exportDialogOpen} onClose={() => setExportDialogOpen(false)}>
              <DialogTitle>Export Activated Subscriptions</DialogTitle>
              <DialogContent>
                <Box display='flex' flexDirection='column' style={{ gap: 16, minWidth: 300 }}>
                  <TextField
                    variant='standard'
                    select
                    label='Filter Date Field'
                    value={exportDateField}
                    onChange={e => setExportDateField(e.target.value as 'created' | 'started' | 'ends')}
                    SelectProps={{ native: true }}
                  >
                    <option value='created'>Purchase Date</option>
                    <option value='started'>Activation Date</option>
                    <option value='ends'>Next Billing Date</option>
                  </TextField>
                  {exportDateField === 'ends' && (
                    <Box style={{ color: '#f57c00', fontSize: 13 }}>
                      Filtering by Next Billing Date may take longer — billing dates can extend years into the future, so the export must scan through all future-dated records to find your range.
                    </Box>
                  )}
                  <TextField
                    variant='standard'
                    type='date'
                    label={`${exportDateField === 'created' ? 'Purchase' : exportDateField === 'started' ? 'Activation' : 'Billing'} Date From`}
                    value={exportDateMin}
                    onChange={e => setExportDateMin(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                  <TextField
                    variant='standard'
                    type='date'
                    label={`${exportDateField === 'created' ? 'Purchase' : exportDateField === 'started' ? 'Activation' : 'Billing'} Date To`}
                    value={exportDateMax}
                    onChange={e => setExportDateMax(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                  <Button
                    onClick={() => {
                      setExportDateMin('')
                      setExportDateMax('')
                      setExportDateField('created')
                    }}>
                    CLEAR DATES (Full Report)
                  </Button>
                </Box>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setExportDialogOpen(false)}>Cancel</Button>
                <ApolloConsumer>
                  {apollo => (
                    <>
                      {exporting ? <LoadMask /> : null}
                      <Button
                        endIcon={<GetApp />}
                        variant='outlined'
                        disabled={exporting}
                        onClick={async () => {
                          if (exporting) return
                          setExporting(true)
                          const dateMin = exportDateMin ? moment(exportDateMin).startOf('day').valueOf() : 0
                          const dateMax = exportDateMax ? moment(exportDateMax).endOf('day').valueOf() : Infinity
                          const hasDateFilter = !!(exportDateMin || exportDateMax)

                          const sortByField =
                            exportDateField === 'created'
                              ? ProductApplicationSortBy.created
                              : exportDateField === 'started'
                              ? ProductApplicationSortBy.effectiveOnOrAfter
                              : ProductApplicationSortBy.expiresOnOrAfter

                          const exportPerPage = exportDateField === 'ends' ? 200 : hasDateFilter ? 100 : 500
                          const results: any[] = []

                          try {
                            let currentPage = 0
                            let done = false

                            while (!done) {
                              const result = await apollo.query({
                                fetchPolicy: 'network-only',
                                query: PLUS_PRODUCT_QUERY,
                                variables: {
                                  ...variables,
                                  sortBy: sortByField,
                                  sortOrder: SortOrder.desc,
                                  perPage: exportPerPage,
                                  page: currentPage
                                }
                              })

                              const apps = result.data.product.listProductApplications.productApplications
                              const pag = result.data.product.listProductApplications.pagination

                              for (const app of apps) {
                                const dateValue =
                                  exportDateField === 'created'
                                    ? app.created
                                    : exportDateField === 'started'
                                    ? app.started
                                    : app.ends

                                if (hasDateFilter && dateValue > dateMax) continue
                                if (hasDateFilter && dateValue < dateMin) {
                                  done = true
                                  break
                                }

                                const flat = createFlatPurchase(app)
                                results.push({
                                  ...flat,
                                  optOutDate: flat.optOutDate ? moment(flat.optOutDate).format('YYYY-MM-DD hh:mm a') : ''
                                })
                              }

                              if ((currentPage + 1) * exportPerPage >= pag.total) {
                                done = true
                              }

                              currentPage++
                            }

                            if (results.length > 0) {
                              const csv = csvFormat(results, Object.keys(results[0]))
                              csvDownload(csv)
                            } else {
                              addToast('No records found in the selected date range', { appearance: 'error' })
                            }
                          } catch (e) {
                            console.error(e)
                            addToast('Error exporting CSV', { appearance: 'error' })
                          }

                          setExporting(false)
                        }}>
                        Export CSV
                      </Button>
                    </>
                  )}
                </ApolloConsumer>
              </DialogActions>
            </Dialog>
          </Box>
        }
      />
    </>
  )
}
